// Calm, spa-style background music, generated live with the Web Audio API so
// there is no audio file to license or download: soft major-seventh pads that
// drift between chords, a slow wash of ocean-like noise, and an occasional
// gentle harp note wandering through a pentatonic scale, all in a warm reverb.
//
// It is on by default in the native app and off by default on the website.
// The choice is remembered per device, and sound pauses while the app is in
// the background.

import { isNativeApp } from "./platform";

const PREF_KEY = "tk_music_enabled";
const MASTER_LEVEL = 0.2;

// C major pentatonic, one octave up and the next, for the harp notes.
const HARP_NOTES = [523.25, 587.33, 659.25, 783.99, 880.0, 1046.5, 1174.66, 1318.51];
// Bright, open chords: Cmaj7, Fmaj7, Am7 (add9), G6. Low root plus three upper notes.
const PAD_CHORDS = [
  [130.81, 261.63, 329.63, 493.88],
  [87.31, 261.63, 349.23, 440.0],
  [110.0, 261.63, 329.63, 493.88],
  [98.0, 246.94, 293.66, 329.63],
];

let ctx = null;
let master = null;
let reverb = null;
let padVoices = [];
let chordIndex = 0;
let chordTimer = null;
let chimeTimer = null;
let harpStep = 2;
let playing = false;
const listeners = new Set();

function readPref() {
  try {
    const v = localStorage.getItem(PREF_KEY);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch { /* storage blocked */ }
  return isNativeApp();
}

function writePref(on) {
  try { localStorage.setItem(PREF_KEY, on ? "1" : "0"); } catch { /* ignore */ }
}

let enabled = readPref();

export function isMusicEnabled() {
  return enabled;
}

export function onMusicChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit() {
  listeners.forEach((fn) => fn(enabled));
}

function makeReverb(seconds = 3.5) {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch);
    // Smooth exponential tail, darker as it fades.
    let lp = 0;
    for (let i = 0; i < len; i++) {
      lp = lp * 0.6 + (Math.random() * 2 - 1) * 0.4;
      data[i] = lp * Math.exp(-4 * i / len);
    }
  }
  const conv = ctx.createConvolver();
  conv.buffer = buf;
  return conv;
}

// Soft, slowly breathing noise that sounds like distant waves.
function makeOcean() {
  const len = ctx.sampleRate * 4;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < len; i++) {
    last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02; // brown noise
    data[i] = last * 3.5;
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  const lowpass = ctx.createBiquadFilter();
  lowpass.type = "lowpass";
  lowpass.frequency.value = 500;
  const swell = ctx.createGain();
  swell.gain.value = 0.05;
  const wave = ctx.createOscillator();
  const waveDepth = ctx.createGain();
  wave.frequency.value = 0.07; // one swell every ~14 seconds
  waveDepth.gain.value = 0.035;
  wave.connect(waveDepth).connect(swell.gain);
  src.connect(lowpass).connect(swell).connect(master);
  src.start();
  wave.start();
}

function build() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return false;
  ctx = new AC();
  master = ctx.createGain();
  master.gain.value = 0;
  master.connect(ctx.destination);

  reverb = makeReverb();
  const wet = ctx.createGain();
  wet.gain.value = 0.45;
  reverb.connect(wet).connect(master);

  // Pad: pure sine voices through a warm lowpass, so nothing buzzes or wavers.
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 1100;
  filter.Q.value = 0.3;

  const padBus = ctx.createGain();
  padBus.gain.value = 0.2;
  filter.connect(padBus);
  padBus.connect(master);
  padBus.connect(reverb);

  padVoices = PAD_CHORDS[0].map((freq, i) => {
    const gain = ctx.createGain();
    gain.gain.value = i === 0 ? 0.22 : 0.12;
    gain.connect(filter);
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;
    osc.connect(gain);
    osc.start();
    // Each voice swells gently on its own slow cycle.
    const breath = ctx.createOscillator();
    const breathDepth = ctx.createGain();
    breath.frequency.value = 0.03 + i * 0.011;
    breathDepth.gain.value = i === 0 ? 0.04 : 0.05;
    breath.connect(breathDepth).connect(gain.gain);
    breath.start();
    return { osc, gain };
  });

  makeOcean();
  return true;
}

function nextChord() {
  chordIndex = (chordIndex + 1) % PAD_CHORDS.length;
  const now = ctx.currentTime;
  PAD_CHORDS[chordIndex].forEach((freq, i) => {
    padVoices[i]?.osc.frequency.setTargetAtTime(freq, now, 3);
  });
}

// One soft harp-like note. Melody moves by small steps, never big jumps.
function chime() {
  const now = ctx.currentTime;
  harpStep = Math.max(0, Math.min(HARP_NOTES.length - 1, harpStep + [-2, -1, -1, 1, 1, 2][(Math.random() * 6) | 0]));
  const freq = HARP_NOTES[harpStep];
  const osc = ctx.createOscillator();
  const octave = ctx.createOscillator();
  const octGain = ctx.createGain();
  const gain = ctx.createGain();
  osc.type = "sine";
  octave.type = "sine";
  osc.frequency.value = freq;
  octave.frequency.value = freq * 2;
  octGain.gain.value = 0.12;
  octave.connect(octGain).connect(gain);
  osc.connect(gain);
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.045, now + 0.04);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 3.5);
  gain.connect(master);
  gain.connect(reverb);
  osc.start(now);
  octave.start(now);
  osc.stop(now + 3.7);
  octave.stop(now + 3.7);
}

function scheduleLoops() {
  clearInterval(chordTimer);
  clearTimeout(chimeTimer);
  chordTimer = setInterval(nextChord, 16000);
  const loop = () => {
    if (playing && ctx.state === "running") {
      chime();
      // Now and then a second note follows, like a slow harp phrase.
      if (Math.random() < 0.35) setTimeout(() => playing && chime(), 700 + Math.random() * 500);
    }
    chimeTimer = setTimeout(loop, 4000 + Math.random() * 5000);
  };
  chimeTimer = setTimeout(loop, 3000);
}

function fadeTo(level, seconds) {
  const now = ctx.currentTime;
  master.gain.cancelScheduledValues(now);
  master.gain.setValueAtTime(master.gain.value, now);
  master.gain.linearRampToValueAtTime(level, now + seconds);
}

async function play() {
  if (!ctx && !build()) return;
  playing = true;
  try { await ctx.resume(); } catch { /* needs a tap first */ }
  fadeTo(MASTER_LEVEL, 4);
  scheduleLoops();
}

function stop() {
  playing = false;
  if (!ctx) return;
  fadeTo(0, 1.2);
  clearInterval(chordTimer);
  clearTimeout(chimeTimer);
  setTimeout(() => { if (!playing) ctx.suspend().catch(() => {}); }, 1400);
}

export function setMusicEnabled(on) {
  enabled = on;
  writePref(on);
  if (on) play(); else stop();
  emit();
}

export function toggleMusic() {
  setMusicEnabled(!enabled);
}

// Start on load when enabled. Browsers and some webviews only allow audio
// after the first touch, so a suspended context is resumed on the first tap.
export function initAmbientMusic() {
  if (enabled) play();
  const unlock = () => {
    if (enabled && ctx && ctx.state !== "running" && !document.hidden) {
      ctx.resume().catch(() => {});
    }
    if (ctx?.state === "running") {
      document.removeEventListener("pointerdown", unlock, true);
      document.removeEventListener("touchend", unlock, true);
    }
  };
  document.addEventListener("pointerdown", unlock, true);
  document.addEventListener("touchend", unlock, true);

  const setActive = (active) => {
    if (!ctx || !enabled) return;
    if (active) ctx.resume().catch(() => {});
    else ctx.suspend().catch(() => {});
  };
  document.addEventListener("visibilitychange", () => setActive(!document.hidden));
  if (isNativeApp()) {
    import("@capacitor/app")
      .then(({ App }) => App.addListener("appStateChange", ({ isActive }) => setActive(isActive)))
      .catch(() => {});
  }
}
